import { useEffect, useRef } from 'react';
import { EditorView, lineNumbers, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { markdown } from '@codemirror/lang-markdown';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { ArticleBody } from './ArticleBody.js';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Optional className applied to the outer wrapper. */
  className?: string;
}

/**
 * CodeMirror-based markdown editor with side-by-side preview.
 * Source-of-truth is the markdown text; preview reuses `<ArticleBody />`
 * so editor preview and live detail view share the render path.
 *
 * Layout: 50/50 split on wide screens, stacked on narrow.
 * No backend change — caller still POSTs the value via PUT /content.
 */
export function MarkdownEditor({ value, onChange, className }: MarkdownEditorProps) {
  const editorParent = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);

  // Keep latest onChange in a ref so the EditorView is created once and
  // doesn't tear down on every parent render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Create the view once.
  useEffect(() => {
    if (!editorParent.current || viewRef.current) return;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        onChangeRef.current(update.state.doc.toString());
      }
    });

    const state = EditorState.create({
      doc: value,
      extensions: [
        lineNumbers(),
        history(),
        markdown(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        EditorView.lineWrapping,
        updateListener,
        EditorView.theme({
          '&': { height: '100%', fontSize: '14px' },
          '.cm-scroller': {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
            lineHeight: '1.6',
          },
          '.cm-content': { padding: '1rem 0' },
          '.cm-gutters': {
            backgroundColor: '#f9fafb',
            color: '#9ca3af',
            border: 'none',
          },
          '.cm-activeLine': { backgroundColor: 'transparent' },
          '.cm-activeLineGutter': { backgroundColor: 'transparent' },
        }),
      ],
    });

    const view = new EditorView({ state, parent: editorParent.current });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes into the editor (e.g. when caller resets).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (view.state.doc.toString() === value) return;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
    });
  }, [value]);

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${className ?? ''}`}>
      <div className="flex flex-col">
        <p className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-1.5">Markdown source</p>
        <div
          ref={editorParent}
          className="border border-grey-200 rounded-lg overflow-hidden h-[45dvh] lg:h-[600px] focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent"
        />
      </div>
      <div className="flex flex-col">
        <p className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-1.5">Preview</p>
        <div className="border border-grey-200 rounded-lg p-4 sm:p-6 h-[45dvh] lg:h-[600px] overflow-y-auto bg-surface">
          {value.trim() ? (
            <ArticleBody content={value} />
          ) : (
            <p className="text-grey-400 italic">Preview appears here as you type.</p>
          )}
        </div>
      </div>
    </div>
  );
}
